import React, { useEffect, useState, useRef, RefObject } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';
import InfiniteScroll from 'react-infinite-scroller';
import _ from 'lodash';
import {
  ChatInputComponent,
  ChatMessageComponent,
} from '../../atoms/atoms.index';
import {
  NavbarComponent,
  NavigationMenuComponent,
} from '../../organisms/organisms.index';
import {
  EChatUserType,
  EChatMessageType,
  IChatMessage,
  IChatMessageDto,
} from '../../../domain/chat.interfaces';
import { IUser } from '../../../domain/user.interface';
import {
  makeSelectMessages,
  makeSelectTotalItems,
} from '../../../store/chat/chat.selectors';
import { makeSelectCurrentUser } from '../../../store/current-user/current-user.selectors';
import {
  loadAllMessages,
  newMessage,
  resetMessages,
  loadLastMessages,
} from '../../../store/chat/chat.actions';
import {
  replaceNewLineWithBR,
  trimEndNewLine,
  transformToPlainText,
} from '../../../utils/formatter';
import './Chat.component.scss';

function MessageList(props: any) {
  const { messages } = props;

  function getDateString(date: string) {
    const dateText =
      date === moment(new Date()).format('DD/MM/YYYY') ? (
        <FormattedMessage id='common.today' />
      ) : date ===
        moment(new Date())
          .subtract(1, 'days')
          .format('DD/MM/YYYY') ? (
        <FormattedMessage id='common.yesterday' />
      ) : (
        date
      );
    return dateText;
  }

  return (
    <div>
      {messages.map((msg: IChatMessage, index: number) => {
        return (
          <React.Fragment key={index}>
            {((messages[index - 1] && messages[index - 1].date !== msg.date) ||
              index === 0) && (
              <div className='bubble-date-container d-flex justify-content-center'>
                <div className='bubble-date'>{getDateString(msg.date)}</div>
              </div>
            )}
            <ChatMessageComponent key={index} message={msg} />
          </React.Fragment>
        );
      })}
    </div>
  );
}

export function ChatComponent(props: any) {
  const dispatch = useDispatch();
  const messages = useSelector(makeSelectMessages());
  const totalItems = useSelector(makeSelectTotalItems());
  const currentUser: IUser = useSelector(makeSelectCurrentUser());
  const { id } = currentUser;
  const groupId = props.match.params.groupId;
  const itemsInPage = 20;
  const messagesEndRef = useRef(null);

  const [chatMessages, setChatMessages] = useState<IChatMessage[]>([]);
  const [message, setMessage] = useState('');
  const [currentChatPage, setCurrentChatPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [pause, setPause] = useState(true);

  function useInterval(callback: any, delay: number | null) {
    const savedCallback = useRef();
    useEffect(() => {
      savedCallback.current = callback;
    });
    useEffect(() => {
      function tick() {
        (savedCallback as React.MutableRefObject<any>).current();
      }
      if (delay !== null) {
        const tId = setInterval(tick, delay);
        return () => clearInterval(tId);
      }
    }, [delay]);
  }

  useInterval(
    () => {
      dispatch(loadLastMessages(groupId));
    },
    pause ? null : 10000,
  );

  useEffect(() => {
    dispatch(loadAllMessages(groupId, itemsInPage, currentChatPage));
    scrollToBottom();
    return () => {
      dispatch(resetMessages());
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setChatMessages(messageFormatter(messages));
    setPause(false);
    // eslint-disable-next-line
  }, [messages]);

  useEffect(() => {
    setHasMore(checkForMoreMessage());
    // eslint-disable-next-line
  }, [totalItems, currentChatPage]);

  function handleInputChange(e: any) {
    setMessage(transformToPlainText(e.target.value));
  }

  function messageFormatter(msgs: IChatMessageDto[]): IChatMessage[] {
    if (!msgs.length) {
      return [];
    }
    return msgs
      .map((msg: IChatMessageDto) => {
        return {
          id: msg.id,
          time: moment(new Date(Date.parse(msg.createDate))).format('HH:mm'),
          date: moment(new Date(Date.parse(msg.createDate))).format('DD/MM/YYYY'),
          name: msg.user.name,
          message: msg.message,
          userType:
            msg.user.id === id ? EChatUserType.SENT : EChatUserType.RECEIVED,
          messageType: msg.messageType,
        };
      })
      .reverse();
  }

  function sendMessage() {
    const body: Partial<IChatMessage> = {
      message: replaceNewLineWithBR(trimEndNewLine(message)),
      messageType: EChatMessageType.TEXT,
    };
    setPause(true);
    setMessage('');
    dispatch(newMessage(groupId, body));
    scrollToBottom();
  }

  function checkForMoreMessage(): boolean {
    const pages = Math.ceil(totalItems / itemsInPage);
    return currentChatPage + 1 < pages ? true : false;
  }

  function loadFunc() {
    if (hasMore) {
      setPause(true);
      dispatch(loadAllMessages(groupId, itemsInPage, currentChatPage + 1));
      setCurrentChatPage(currentChatPage + 1);
    } else {
      return;
    }
  }

  const debounceLoadFunc = _.debounce(loadFunc, 3000);

  const scrollToBottom = () => {
    if (messagesEndRef === null) {
      return;
    }
    setTimeout(
      () => (messagesEndRef as RefObject<any>).current.scrollIntoView(false),
      750,
    );
  };

  return (
    <div className='chat-page'>
      <div className='navs'>
        <NavbarComponent />
        <NavigationMenuComponent {...props} />
      </div>
      <div className='chat-container'>
        <div className='chat-wrapper d-flex flex-column justify-content-between'>
          <div className='conversation-container d-flex flex-column justify-content-end'>
            <div className='scroll-container'>
              <InfiniteScroll
                pageStart={0}
                loadMore={debounceLoadFunc}
                hasMore={hasMore}
                loader={
                  <div
                    key={1986}
                    className='d-flex justify-content-center align-items-center chat-spinner'
                  >
                    <span className='spinner-border text-primary' role='status'>
                      <span className='sr-only'>Loading...</span>
                    </span>
                  </div>
                }
                useWindow={true}
                initialLoad={false}
                threshold={50}
                isReverse={true}
              >
                {!!chatMessages.length && (
                  <MessageList messages={chatMessages} />
                )}
                <div id='messages-end' ref={messagesEndRef} />
              </InfiniteScroll>
            </div>
          </div>
        </div>
        <ChatInputComponent
          sendMessage={sendMessage}
          handleInputChange={handleInputChange}
          message={message}
        />
      </div>
    </div>
  );
}
